import { z } from "zod";
import { compareText, fail, ok, type FieldKind } from "../kind";
import { scalarToText } from "./text";

/**
 * Des lignes d'une autre Database (cf. spec, catalogue des champs : `relation`,
 * même canvas en v1), par id de nodeData. Bidirectionnelle quand
 * `reverseFieldId` nomme le champ qui lui répond dans la Database cible :
 * les deux restent d'accord à chaque écriture (cf. `RelationModels`).
 * L'appartenance des ids à la Database cible se vérifie à l'écriture.
 */
export const relationSettings = z.object({
  // La Database cible, par l'id de son nodeData. Vide : pas encore choisie.
  databaseId: z.string().default(""),
  multiple: z.boolean().default(true),
  // Le champ inverse, dans la Database cible.
  reverseFieldId: z.string().optional(),
  // Demande, à la création, d'un champ inverse de ce nom (consommée par le
  // serveur, qui pose alors `reverseFieldId`).
  reverseName: z.string().optional(),
});

const MAX_REFS = 100;
const idSchema = z.string().min(1).max(64);

export const relationKind: FieldKind<"relation"> = {
  type: "relation",
  settings: relationSettings,
  value: z.array(idSchema).min(1).max(MAX_REFS),
  coerce(input, settings) {
    const raw = Array.isArray(input)
      ? input
      : typeof input === "string"
        ? input.split(/[,;\s]+/)
        : [input];
    const ids: string[] = [];
    for (const item of raw) {
      const id = scalarToText(item)?.trim();
      if (id === undefined) return fail("Expected row ids.");
      if (id === "") continue;
      if (!idSchema.safeParse(id).success) return fail(`"${id}" is not a row id.`);
      if (!ids.includes(id)) ids.push(id);
    }
    if (ids.length === 0) return ok(null);
    if (!settings.multiple && ids.length > 1) return fail("Only one row can be linked.");
    if (ids.length > MAX_REFS) return fail(`At most ${MAX_REFS} rows.`);
    return ok(ids);
  },
  isEmpty: (value) => value.length === 0,
  // En `plain`, les ids : relisibles par `coerce`. Sinon les titres des
  // lignes, quand l'hôte sait les résoudre.
  format: (value, _settings, ctx) =>
    ctx.style === "plain"
      ? value.join(", ")
      : value.map((id) => ctx.resolveNodeTitle?.(id) ?? id).join(", "),
  compare: (a, b) => compareText(a[0] ?? "", b[0] ?? "") || a.length - b.length,
  filters: [
    { id: "is_any_of", operand: "values", test: (v, o: string[]) => v.some((id) => o.includes(id)) },
    {
      id: "is_none_of",
      operand: "values",
      matchesEmpty: true,
      test: (v, o: string[]) => !v.some((id) => o.includes(id)),
    },
  ],
  aggregates: ["count", "count_empty", "count_not_empty", "percent_empty", "percent_not_empty"],
  // Ce que l'agent écrit : l'id d'une ligne de la Database cible (la colonne
  // rowId de `nole table` sur elle).
};
